import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizePhone, PHONE_ERROR, PHONE_ALLOWED } from '../../utils/phone';
import './index.css';

const ContactForm = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    emailAddress: '',
    phoneNumber: '',
    purpose: '',
    education: '',
    graduationYear: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [graduationYearOpen, setGraduationYearOpen] = useState(false);
  const [educationOpen, setEducationOpen] = useState(false);
  const graduationYearRef = useRef(null);
  const educationRef = useRef(null);

  const educationOptions = ['B.Tech', 'M.Tech', 'BCA', 'Bcom', 'MCA', 'Others'];
  
  // Generate graduation years from 2017 to 2030
  const graduationYears = Array.from({ length: 14 }, (_, i) => 2017 + i);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (graduationYearRef.current && !graduationYearRef.current.contains(event.target)) {
        setGraduationYearOpen(false);
      }
      if (educationRef.current && !educationRef.current.contains(event.target)) {
        setEducationOpen(false);
      }
    };

    if (graduationYearOpen || educationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [graduationYearOpen, educationOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!normalizePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = PHONE_ERROR;
    }

    if (!formData.education) {
      newErrors.education = 'Education is required';
    }

    if (!formData.graduationYear.trim()) {
      newErrors.graduationYear = 'Graduation year is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validate()) {
      try {
        // API URL configuration
        // In development: http://localhost:8000/api
        // In production: /api (relative path, same domain)
        const API_URL = process.env.REACT_APP_API_URL || 
          (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');
        const response = await fetch(`${API_URL}/contact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const data = await response.json();

        if (response.ok) {
          console.log('Contact saved successfully:', data);
          setSubmitted(true);
          
          // Reset form after 3 seconds
          setTimeout(() => {
            setFormData({
              name: '',
              emailAddress: '',
              phoneNumber: '',
              purpose: '',
              education: '',
              graduationYear: '',
              message: ''
            });
            setSubmitted(false);
          }, 3000);
        } else {
          // Handle API errors
          console.error('Error submitting form:', data.message);
          alert(`Error: ${data.message || 'Failed to submit form'}`);
        }
      } catch (error) {
        console.error('Network error:', error);
        alert('Network error: Could not connect to server. Please make sure the server is running.');
      }
    }
  };

  const formContent = (
    <>
      {!embedded && (
        <>
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">Fill out the form below and we'll get back to you soon!</p>
        </>
      )}
      
      {submitted ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Thank you for contacting us!</h2>
            <p>We've received your message and will get back to you shortly.</p>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="emailAddress">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="emailAddress"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  className={errors.emailAddress ? 'error' : ''}
                  placeholder="Enter your email address"
                />
                {errors.emailAddress && <span className="error-message">{errors.emailAddress}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phoneNumber">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => PHONE_ALLOWED.test(e.target.value) && handleChange(e)}
                  className={errors.phoneNumber ? 'error' : ''}
                  placeholder="+91 98765 43210"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={20}
                />
                {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="purpose">
                  Purpose <span className="optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder="What is the purpose of your inquiry?"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group custom-dropdown-wrapper">
                <label htmlFor="education">
                  Education <span className="required">*</span>
                </label>
                <div className="custom-dropdown" ref={educationRef}>
                  <div
                    className={`custom-dropdown-select ${errors.education ? 'error' : ''} ${educationOpen ? 'open' : ''}`}
                    onClick={() => setEducationOpen(!educationOpen)}
                  >
                    <span className={formData.education ? 'selected-value' : 'placeholder'}>
                      {formData.education || 'Select your education'}
                    </span>
                    <span className="dropdown-arrow">▼</span>
                  </div>
                  {educationOpen && (
                    <div className="custom-dropdown-menu">
                      {educationOptions.map((option, index) => (
                        <div
                          key={index}
                          className={`custom-dropdown-option ${formData.education === option ? 'selected' : ''}`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, education: option }));
                            setEducationOpen(false);
                            if (errors.education) {
                              setErrors(prev => ({ ...prev, education: '' }));
                            }
                          }}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.education && <span className="error-message">{errors.education}</span>}
              </div>

              <div className="form-group custom-dropdown-wrapper">
                <label htmlFor="graduationYear">
                  Graduation Year <span className="required">*</span>
                </label>
                <div className="custom-dropdown" ref={graduationYearRef}>
                  <div
                    className={`custom-dropdown-select ${errors.graduationYear ? 'error' : ''} ${graduationYearOpen ? 'open' : ''}`}
                    onClick={() => setGraduationYearOpen(!graduationYearOpen)}
                  >
                    <span className={formData.graduationYear ? 'selected-value' : 'placeholder'}>
                      {formData.graduationYear || 'Select graduation year'}
                    </span>
                    <span className="dropdown-arrow">▼</span>
                  </div>
                  {graduationYearOpen && (
                    <div className="custom-dropdown-menu">
                      {graduationYears.map((year) => (
                        <div
                          key={year}
                          className={`custom-dropdown-option ${formData.graduationYear === year.toString() ? 'selected' : ''}`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, graduationYear: year.toString() }));
                            setGraduationYearOpen(false);
                            if (errors.graduationYear) {
                              setErrors(prev => ({ ...prev, graduationYear: '' }));
                            }
                          }}
                        >
                          {year}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.graduationYear && <span className="error-message">{errors.graduationYear}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message">
                Message <span className="optional">(Optional)</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                placeholder="Enter your message here..."
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-button">
                Submit
              </button>
              {!embedded && (
                <button type="button" className="go-back-button" onClick={() => navigate('/')}>
                  Go Back
                </button>
              )}
            </div>
          </form>
        )}
    </>
  );

  if (embedded) {
    return (
      <>
        <div className="contact-form-container">
          {formContent}
        </div>
      </>
    );
  }

  return (
    <div className="contact-form-page">
      <div className="contact-form-container">
        {formContent}
      </div>
    </div>
  );
};

export default ContactForm;

